import { PostalCodeField } from 'components/PostalCodeField'
import { StateProvinceSelect } from 'components/ProvinceSelect'
import React, { ReactElement, useState } from 'react'
import { AvForm, AvField } from 'availity-reactstrap-validation'
import { Button, Fade, UncontrolledAlert } from 'reactstrap'
import { Address, Owner, OwnerOwnerCategoryEnum } from 'api'
import { OnboardingPageProps } from '../../onboardingManager'
import { FormServerErrors } from 'components/FormServerErrors'
import { t } from 'i18next'
import { addOwnerToParent } from '../uboManagerHelper'
import { getControlPersonPositions } from 'app/selectors'
import { useSelector } from 'react-redux'
export interface IndividualShareholder {
  share: number
  name: string
  firstName: string
  lastName: string
  dateOfBirth: string
  ownerCategory: OwnerOwnerCategoryEnum
  addressesAttributes: {
    address1: string
    city: string
    provinceCode: string
    postalCode: string
  }
}

interface IndividualShareholderFormProps extends OnboardingPageProps {
  onSuccess: () => void
  onCancel: () => void
}

const defaultAddress: Address = {
  address1: '',
  city: '',
  provinceCode: '',
  postalCode: '',
}

export function CreateControlPersonForm({
  onNext,
  handleFieldChange,
  serverErrors,
  setServerErrors,
  validate,
  setSteps,
  activeClient,
  formRef,
  parentOwner,
  onSuccess,
  onCancel,
}: IndividualShareholderFormProps): ReactElement {
  const newOwnerDefaults: Owner = {
    share: 0,
    name: '',
    firstName: '',
    lastName: '',
    ownerCategory: OwnerOwnerCategoryEnum.ControlPerson,
    dateOfBirth: '2000-01-01',
    addressesAttributes: [defaultAddress],
  }
  const controlPersonPositions: string[] = useSelector(getControlPersonPositions)
  const [success, setSuccess] = useState(false)

  const handleValidSubmit = (event, values) => {
    onNext({
      e: event,
      ownersValues: [
        ...addOwnerToParent(activeClient.ownersAttributes ?? [], parentOwner?.id ?? null, {
          ...values,
          ownerCategory: OwnerOwnerCategoryEnum.ControlPerson,
        }),
      ],
    })?.then((response) => {
      if (response?.body?.status !== 200) {
        setSuccess(false)
        return
      } else {
        setTimeout(() => setSuccess(false), 5000)
        onSuccess()
        setSuccess(true)
      }
    })
  }

  return (
    <>
      <Fade in={success} mountOnEnter={true} unmountOnExit={true}>
        <UncontrolledAlert>Account Update Successful</UncontrolledAlert>
      </Fade>
      <FormServerErrors errors={(serverErrors && serverErrors['base']) || []} />
      <AvForm
        formRef={formRef}
        onValidSubmit={(event, values) => handleValidSubmit(event, values)}
        model={newOwnerDefaults}
      >
        <h3>{`Control Person for ${activeClient?.operatingAs}`}</h3>

        <AvField
          name="position"
          label={'Position'}
          type="select"
          required
          validate={{
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
            async: validate,
          }}
          onChange={handleFieldChange}
        >
          <option value="" disabled>
            Select a position
          </option>
          {controlPersonPositions?.map((position) => (
            <option key={position} value={position}>
              {position}
            </option>
          ))}
        </AvField>

        <AvField
          name="firstName"
          type="text"
          label="First Name"
          className="mb-2"
          required
          onChange={handleFieldChange}
          validate={{
            async: validate,
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
          }}
        />
        <AvField
          name="lastName"
          type="text"
          label="Last Name"
          className="mb-2"
          required
          onChange={handleFieldChange}
          validate={{
            async: validate,
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
          }}
        />
        <AvField
          name="dateOfBirth"
          type="date"
          label="Date of Birth"
          className="mb-2"
          required
          onChange={handleFieldChange}
          validate={{
            async: validate,
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
            dateRange: {
              start: { value: -200, units: 'years' },
              end: { value: 0, units: 'days' },
              errorMessage: 'Date of birth must in the past',
            },
          }}
        />
        <AvField
          name="addressesAttributes[0].address1"
          type="text"
          label="Personal Street Address"
          placeholder="Ex: 123 First Street"
          className="mb-2"
          required
          onChange={handleFieldChange}
          validate={{
            async: validate,
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
          }}
        />
        <AvField
          name="addressesAttributes[0].city"
          type="text"
          label="Town or City"
          placeholder="Enter the town or city"
          className="mb-2"
          required
          onChange={handleFieldChange}
          validate={{
            async: validate,
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
          }}
        />
        <StateProvinceSelect
          required
          name="addressesAttributes[0].provinceCode"
          onChange={handleFieldChange}
          countryCode={activeClient?.countryCode || 'CA'}
          placeholder="Choose one..."
        />
        <PostalCodeField
          required
          name="addressesAttributes[0].postalCode"
          countryCode={activeClient?.countryCode || 'CA'}
          onChange={handleFieldChange}
          examplePlaceholder={true}
        />

        <div className="d-flex justify-content-between mt-4">
          <Button type="button" color="primary" className="btn-rotessa-secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" color="primary" className="btn-rotessa-primary">
            Submit
          </Button>
        </div>
      </AvForm>
    </>
  )
}
